import { useState, type ReactNode } from "react";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import FilterAlt from "@mui/icons-material/FilterAlt";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Pagination,
  PaginationItem,
  Paper,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import type { Promisable } from "type-fest";

import type { SortDirection } from "@thalamos/haven-common";

export type DirectorySort<Id extends string = string> = {
  column: Id;
  direction: SortDirection;
};

/** A column's filter: whether it is narrowing the listing, and the panel that sets it. */
export type DirectoryColumnFilter = {
  active: boolean;
  panel: ReactNode;
  /** Called as the panel opens, so a caller can fetch what it offers. */
  onOpen?: () => Promisable<void>;
};

export type DirectoryColumn<Row, Id extends string = string> = {
  id: Id;
  labelKey: string;
  render: (row: Row) => ReactNode;
  sortable?: boolean;
  filter?: DirectoryColumnFilter;
};

/** Default, then ascending, then descending, then back to default. */
export const nextSort = <Id extends string>(
  current: DirectorySort<Id> | null,
  column: Id,
): DirectorySort<Id> | null => {
  if (current?.column !== column) {
    return { column, direction: "ascending" };
  }

  return current.direction === "ascending"
    ? { column, direction: "descending" }
    : null;
};

const SortControl = ({
  label,
  direction,
  onClick,
}: {
  label: string;
  direction: SortDirection | undefined;
  onClick: () => Promisable<void>;
}) => {
  const { t } = useTranslation();

  const sorted = direction !== undefined;
  const arrowSx = { fontSize: 16 };

  return (
    <ButtonBase
      onClick={() => {
        void onClick();
      }}
      aria-label={t("admin.sortBy", { column: label })}
      sx={(theme) => ({
        borderRadius: 1,
        px: 1,
        py: 0.5,
        gap: 0.5,
        font: "inherit",
        whiteSpace: "nowrap",
        "&:hover": { bgcolor: theme.palette.action.hover },
        ...(sorted
          ? {
              color: theme.palette.primary.main,
              fontWeight: 600,
              bgcolor: theme.palette.tonal.primary.background,
              "&:hover": { bgcolor: theme.palette.tonal.primary.background },
            }
          : {}),
      })}
    >
      {label}
      {direction === "ascending" ? (
        <ArrowUpward aria-hidden sx={arrowSx} />
      ) : direction === "descending" ? (
        <ArrowDownward aria-hidden sx={arrowSx} />
      ) : (
        <UnfoldMore aria-hidden color="disabled" sx={arrowSx} />
      )}
    </ButtonBase>
  );
};

const FilterControl = ({
  label,
  filter,
}: {
  label: string;
  filter: DirectoryColumnFilter;
}) => {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = anchor !== null;

  return (
    <>
      <IconButton
        size="small"
        sx={(theme) => ({
          width: 28,
          height: 28,
          borderRadius: 1,
          ...(filter.active
            ? {
                bgcolor: theme.palette.tonal.primary.background,
                "&:hover": { bgcolor: theme.palette.tonal.primary.background },
              }
            : {}),
        })}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={
          filter.active
            ? t("admin.filteredBy", { column: label })
            : t("admin.filterBy", { column: label })
        }
        onClick={(event) => {
          setAnchor(event.currentTarget);
          void filter.onOpen?.();
        }}
      >
        {filter.active ? (
          <FilterAlt color="primary" sx={{ fontSize: 18 }} />
        ) : (
          <FilterAltOutlined sx={{ fontSize: 18 }} />
        )}
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": t("admin.filterBy", { column: label }),
          },
        }}
      >
        <Box sx={{ p: 2, minWidth: 280 }}>{filter.panel}</Box>
      </Popover>
    </>
  );
};

/**
 * A page of an administrative listing.
 *
 * Shared by both directories because the mechanics — a loading state, an empty
 * state, a pager driven by a server-side total — are the same, and having them in
 * one place is what stops one listing quietly behaving differently from the other.
 *
 * `total` is the size of the whole result set rather than of `rows`: the pager
 * cannot say "of 9 pages" from a page, and a client-side count would be wrong the
 * moment the result set is larger than one page.
 */
export const DirectoryTable = <Row, Id extends string = string>({
  rows,
  total,
  loading,
  error,
  columns,
  rowKey,
  limit,
  offset,
  onPageChange,
  sort = null,
  onSortChange,
  rowSx,
}: {
  rows: readonly Row[];
  total: number;
  loading: boolean;
  error: boolean;
  columns: readonly DirectoryColumn<Row, Id>[];
  rowKey: (row: Row) => string;
  /** How a row stands out, where some rows need to. */
  rowSx?: (row: Row) => SxProps<Theme> | undefined;
  limit: number;
  offset: number;
  onPageChange: (offset: number) => void;
  /** The order the listing is in, or `null` for its default. */
  sort?: DirectorySort<Id> | null;
  onSortChange?: (sort: DirectorySort<Id> | null) => Promisable<void>;
}) => {
  const { t } = useTranslation();
  const pages = Math.ceil(total / limit);

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {t("admin.loadFailed")}
      </Typography>
    );
  }

  return (
    <Stack spacing={1}>
      {/* Bounded so the heading row stays in view while the rows scroll
          beneath it. */}
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ maxHeight: "max(24rem, 70vh)" }}
      >
        {/* The page around it may let text break anywhere, which would squeeze
            every column to a letter's width rather than let the table scroll
            sideways. */}
        <Table size="small" stickyHeader sx={{ overflowWrap: "normal" }}>
          <TableHead>
            <TableRow>
              {columns.map((column) => {
                const label = t(column.labelKey);
                const direction =
                  sort?.column === column.id ? sort.direction : undefined;

                return (
                  <TableCell
                    key={column.id}
                    aria-sort={direction}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {column.sortable === true &&
                      onSortChange !== undefined ? (
                        <SortControl
                          label={label}
                          direction={direction}
                          onClick={() =>
                            onSortChange(nextSort(sort, column.id))
                          }
                        />
                      ) : (
                        label
                      )}
                      {column.filter === undefined ? null : (
                        <FilterControl label={label} filter={column.filter} />
                      )}
                    </Stack>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Loading and empty are told apart deliberately: an administrator
                who reads "none found" while the request is still in flight has
                been told something untrue about the data. */}
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box sx={{ py: 3 }}>
                    <CircularProgress
                      size={24}
                      role="status"
                      aria-label={t("common.loading")}
                    />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length}>
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.noResults")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={rowKey(row)} hover sx={rowSx?.(row)}>
                  {columns.map((column) => (
                    <TableCell key={column.id}>{column.render(row)}</TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Stack alignItems="center" spacing={0.5}>
        <Pagination
          color="primary"
          shape="rounded"
          count={Math.max(pages, 1)}
          disabled={pages <= 1}
          page={Math.floor(offset / limit) + 1}
          onChange={(_event, page) => {
            onPageChange((page - 1) * limit);
          }}
          renderItem={(item) => (
            <PaginationItem
              {...item}
              slots={{ previous: ArrowBack, next: ArrowForward }}
            />
          )}
        />
        <Typography variant="body2" color="text.secondary">
          {t("admin.pageRange", {
            from: total === 0 ? 0 : offset + 1,
            to: Math.min(offset + limit, total),
            total,
          })}
        </Typography>
      </Stack>
    </Stack>
  );
};
